import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Info, Loader2, Search, ShieldAlert, Tag, X } from 'lucide-react';
import {
  binder, eisfSearch, regulatoryTags, type SearchDocumentsParams,
} from '@/api/client';
import { humanStatus } from '@/lib/humanise';
import { PageHeader } from '@/components/layout/PageHeader';
import { DocumentViewerModal } from '@/components/binder/DocumentViewerModal';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

/**
 * Sprint-18 (eISF spec §functional cap #5) — advanced search page.
 *
 * Cross-binder search scoped to the active site. Combine text + status +
 * tag + PHI + expiry filters. Results link into the document viewer
 * modal so the user can preview without leaving the page.
 */

export default function EisfSearchPage() {
  const [text, setText] = useState('');
  const [phiOnly, setPhiOnly] = useState(false);
  const [expiresWithin, setExpiresWithin] = useState<number | ''>('');
  const [tagIds, setTagIds] = useState<Set<string>>(new Set());
  const [statuses, setStatuses] = useState<Set<string>>(new Set());
  const [viewerDoc, setViewerDoc] = useState<{ binderRef: string; docRef: string } | null>(null);

  const tagsQ = useQuery({ queryKey: ['regulatory-tags'], queryFn: regulatoryTags.list, staleTime: 60_000 });
  const bindersQ = useQuery({ queryKey: ['binder', 'list'], queryFn: binder.list, staleTime: 60_000 });

  const bindersByUuid = useMemo(() => {
    const m = new Map<string, { ref: string | null }>();
    for (const b of bindersQ.data?.binders ?? []) m.set(b.binder_id, { ref: b.reference_number ?? null });
    return m;
  }, [bindersQ.data]);

  const params: SearchDocumentsParams = useMemo(() => ({
    ...(text.trim() ? { text: text.trim() } : {}),
    ...(phiOnly ? { phi_only: true } : {}),
    ...(typeof expiresWithin === 'number' ? { expires_within_days: expiresWithin } : {}),
    ...(tagIds.size > 0 ? { tag_ids: [...tagIds] } : {}),
    ...(statuses.size > 0 ? { statuses: [...statuses] } : {}),
    limit: 100,
  }), [text, phiOnly, expiresWithin, tagIds, statuses]);

  const searchQ = useQuery({
    queryKey: ['eisf', 'search', params],
    queryFn: () => eisfSearch.documents(params),
    staleTime: 0,
  });

  function toggleTag(id: string) {
    setTagIds((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  function toggleStatus(s: string) {
    setStatuses((prev) => { const n = new Set(prev); if (n.has(s)) n.delete(s); else n.add(s); return n; });
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Search documents"
        subtitle="Find documents across every binder at this site. Combine text, tags, statuses, and expiry-window filters to narrow the results."
      />

      <div className="rounded border border-surface-border bg-white p-4 space-y-4 mb-5">
        <div className="flex items-center gap-2 rounded border border-surface-border bg-white px-3 h-10">
          <Search className="h-4 w-4 text-ink-muted" />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Search by title or reference number…"
            className="flex-1 bg-transparent outline-none text-sm"
          />
          {text && (
            <button type="button" onClick={() => setText('')} className="text-ink-muted hover:text-ink-primary">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={phiOnly} onChange={(e) => setPhiOnly(e.target.checked)} />
            <ShieldAlert className="h-3.5 w-3.5 text-amber-700" />
            PHI only
          </label>
          <label className="flex items-center gap-2">
            Expiring within
            <select
              value={expiresWithin === '' ? '' : String(expiresWithin)}
              onChange={(e) => setExpiresWithin(e.target.value === '' ? '' : Number(e.target.value))}
              className="border border-surface-border rounded px-2 py-1 text-sm"
            >
              <option value="">— any time —</option>
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="180">6 months</option>
            </select>
          </label>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-1.5">Status</div>
          <div className="flex flex-wrap gap-1.5">
            {(['draft', 'preview', 'in_approval', 'current', 'superseded', 'archived'] as const).map((s) => {
              const on = statuses.has(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={
                    'rounded-full px-2.5 py-1 text-xs ' +
                    (on
                      ? 'bg-accent-500 text-white'
                      : 'bg-surface-muted/40 text-ink-secondary hover:bg-surface-muted/70')
                  }
                >
                  {humanStatus(s)}
                </button>
              );
            })}
          </div>
        </div>

        {tagsQ.data && tagsQ.data.tags.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-1.5 flex items-center gap-1.5">
              <Tag className="h-3 w-3" /> Tags
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tagsQ.data.tags.map((t) => {
                const on = tagIds.has(t.tag_id);
                return (
                  <button
                    key={t.tag_id}
                    type="button"
                    onClick={() => toggleTag(t.tag_id)}
                    className={
                      'rounded-full px-2.5 py-1 text-xs ' +
                      (on
                        ? 'bg-accent-500 text-white'
                        : 'bg-surface-muted/40 text-ink-secondary hover:bg-surface-muted/70')
                    }
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="rounded border border-surface-border bg-white overflow-hidden">
        {searchQ.isLoading ? (
          <div className="p-10 text-center text-ink-muted flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Searching…
          </div>
        ) : searchQ.isError ? (
          <div className={`p-6 flex items-start gap-2 text-sm ${errorToneClass(searchQ.error)}`}>
            {isPermissionError(searchQ.error) ? <Info className="h-4 w-4 mt-0.5"/> : <AlertTriangle className="h-4 w-4 mt-0.5" />}
            {errorText(searchQ.error, 'Search failed')}
          </div>
        ) : !searchQ.data || searchQ.data.rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink-muted">
            No documents match the current filters.
          </div>
        ) : (
          <>
            <div className="px-4 py-2 bg-surface-muted/30 text-xs text-ink-muted border-b border-surface-border">
              {searchQ.data.total} result{searchQ.data.total === 1 ? '' : 's'}
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-ink-muted border-b border-surface-border">
                <tr>
                  <th className="px-4 py-2">Document</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Binder</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Expires</th>
                </tr>
              </thead>
              <tbody>
                {searchQ.data.rows.map((r) => {
                  const binderInfo = bindersByUuid.get(r.binder_id);
                  const binderRef = r.binder_reference_number ?? binderInfo?.ref ?? r.binder_id;
                  return (
                    <tr key={r.document_id} className="border-b border-surface-border/30 hover:bg-surface-muted/20">
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => setViewerDoc({ binderRef, docRef: r.reference_number ?? r.document_id })}
                          className="text-left text-accent-700 hover:underline font-medium"
                        >
                          {r.title}
                        </button>
                        {r.reference_number && (
                          <div className="font-mono text-[10px] text-ink-muted">{r.reference_number}</div>
                        )}
                        {r.phi_present && (
                          <span className="inline-flex items-center gap-0.5 mt-0.5 text-[10px] text-amber-800">
                            <ShieldAlert className="h-2.5 w-2.5" /> PHI
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs text-ink-muted">{r.document_type_name}</td>
                      <td className="px-4 py-2">
                        <Link to={`/binder/${encodeURIComponent(binderRef)}`} className="text-xs font-mono text-accent-700 hover:underline">
                          {binderRef}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-xs">{humanStatus(r.status)}</td>
                      <td className="px-4 py-2 text-xs text-ink-muted">
                        {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>

      <DocumentViewerModal
        binderRef={viewerDoc?.binderRef ?? ''}
        documentRef={viewerDoc?.docRef ?? ''}
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
      />
    </div>
  );
}
